import { useMemo } from 'react';
import { SelectedMethodInfo, ConditionInfo } from '../BulkEditModal';
import { getConditionKey, getConditionDisplayText } from '../utils/conditionUtils';

export interface AggregatedCondition extends ConditionInfo {
  key: string;
  methodCount: number;
  methodIds: string[];
  displayText: string;
}

const EXCLUDED_CONDITION_NAMES = ['locations'];

export const useConditionsAggregation = (selectedMethods: SelectedMethodInfo[]) => {
  const aggregatedConditions = useMemo(() => {
    const conditionMap = new Map<string, AggregatedCondition>();

    selectedMethods.forEach((method) => {
      if (!method.conditions) return;

      method.conditions
        .filter((condition) => !EXCLUDED_CONDITION_NAMES.includes(condition.name))
        .forEach((condition) => {
          const key = getConditionKey(condition);

          if (conditionMap.has(key)) {
            const existing = conditionMap.get(key)!;
            if (!existing.methodIds.includes(method.id)) {
              existing.methodCount++;
              existing.methodIds.push(method.id);
            }
          } else {
            conditionMap.set(key, {
              ...condition,
              key,
              methodCount: 1,
              methodIds: [method.id],
              displayText: getConditionDisplayText(condition),
            });
          }
        });
    });

    return Array.from(conditionMap.values()).sort((a, b) => b.methodCount - a.methodCount);
  }, [selectedMethods]);

  const totalConditionsCount = useMemo(() => {
    return selectedMethods.reduce(
      (sum, method) => sum + (method.conditions?.filter((c) => !EXCLUDED_CONDITION_NAMES.includes(c.name)).length || 0),
      0,
    );
  }, [selectedMethods]);

  return {
    aggregatedConditions,
    totalConditionsCount,
    hasConditions: aggregatedConditions.length > 0,
  };
};
